import type { CSSProperties } from 'react'
import { TONE_COLOUR, agoLong, agoShort, nzWhen, useFreshness, type FreshnessRow, type FreshnessSource, type FreshnessTone } from './freshness'

// The "How up to date is your data" list: one row per source, plain words,
// when it last updated (NZ time), how often it should, and a dot. Shared by
// the Data page (variant "full") and the top-bar pill's panel ("compact").
// Reusable by any Command Centre: hand it that customer's source list.

function Dot({ tone, size = 9 }: { tone: FreshnessTone; size?: number }) {
  return (
    <i
      aria-hidden
      style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: TONE_COLOUR[tone], flexShrink: 0 }}
    />
  )
}

function whenText(row: FreshnessRow, now: number, short: boolean): { main: string; clock?: string } {
  if (row.source.notConnected) return { main: 'Not connected yet' }
  if (row.reading === undefined) return { main: 'Checking…' }
  if (row.reading === null) return { main: 'Could not check just now' }
  if (!row.reading.at) return { main: 'Never' }
  return { main: short ? agoShort(row.reading.at, now) : agoLong(row.reading.at, now), clock: nzWhen(row.reading.at, now) }
}

export function FreshnessLegend({ style }: { style?: CSSProperties }) {
  const items: Array<[FreshnessTone, string]> = [
    ['green', 'On time'],
    ['amber', 'Late, up to twice its usual gap'],
    ['red', 'Well overdue, or stopped'],
    ['grey', 'Not connected yet'],
  ]
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px] text-text-secondary" style={style}>
      {items.map(([t, l]) => (
        <span key={t} className="inline-flex items-center gap-1.5">
          <Dot tone={t} />
          {l}
        </span>
      ))}
    </div>
  )
}

/** The full list, for the top of a Data page. */
export function DataFreshness({ sources }: { sources: FreshnessSource[] }) {
  const { rows, now } = useFreshness(sources)
  return (
    <div className="border-t border-border">
      {rows.map((row) => {
        const w = whenText(row, now, false)
        const muted = row.source.notConnected
        return (
          <div
            key={row.source.id}
            className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-b border-border py-3"
            style={muted ? { opacity: 0.75 } : undefined}
          >
            <span className="flex min-w-[260px] flex-[1.3] items-baseline gap-2.5">
              <span style={{ alignSelf: 'center' }}>
                <Dot tone={row.tone} size={10} />
              </span>
              <span className="min-w-0">
                <span className="block text-[15.5px] font-medium text-text-primary">{row.source.name}</span>
                <span className="block text-[13px] leading-snug text-text-secondary">{row.source.what}</span>
              </span>
            </span>
            <span className="min-w-[200px] flex-1">
              <span className="block text-[15px] font-medium" style={{ color: muted ? undefined : row.tone === 'red' ? TONE_COLOUR.red : undefined }}>
                {w.main}
              </span>
              {w.clock && <span className="block text-[12.5px] text-text-secondary">{w.clock}</span>}
              {row.reading?.detail && (
                <span className="block text-[12.5px] font-medium" style={{ color: row.tone === 'red' ? TONE_COLOUR.red : undefined }}>
                  {row.reading.detail}
                </span>
              )}
            </span>
            <span className="min-w-[150px] flex-[0.8] text-[13px] text-text-secondary">
              <span className="block text-[11px] font-bold uppercase tracking-[.1em] text-text-muted">Updates</span>
              {row.source.notConnected ? 'Once it is connected' : row.source.cadence}
            </span>
            <span className="min-w-[130px] text-[13px] font-medium" style={{ color: TONE_COLOUR[row.tone] }}>
              {row.status}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/** The compact list inside the top-bar pill's panel. */
export function DataFreshnessCompact({ rows, now }: { rows: FreshnessRow[]; now: number }) {
  return (
    <ul className="m-0 list-none p-0">
      {rows.map((row) => {
        const w = whenText(row, now, true)
        return (
          <li key={row.source.id} className="flex items-start gap-2.5 border-b border-border px-4 py-2 last:border-b-0">
            <span className="pt-[5px]">
              <Dot tone={row.tone} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-3">
                <span className="truncate text-[13.5px] font-medium text-text-primary">{row.source.name}</span>
                <span
                  className="shrink-0 text-[12.5px] font-medium"
                  style={{ color: row.source.notConnected ? undefined : TONE_COLOUR[row.tone] }}
                >
                  {w.main}
                </span>
              </span>
              <span className="block text-[12px] leading-snug text-text-secondary">
                {row.source.notConnected
                  ? row.source.what
                  : [row.source.cadence, w.clock].filter(Boolean).join(' · ')}
              </span>
              {row.reading?.detail && (
                <span className="block text-[12px] font-medium" style={{ color: row.tone === 'red' ? TONE_COLOUR.red : undefined }}>
                  {row.reading.detail}
                </span>
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
